import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@astro/auth'
import { LuLock, LuSend } from 'react-icons/lu'
import { useLiveStreamChat } from './liveStreamShared'

/**
 * The live chat beside the player.
 *
 * This is where "watch freely, sign in to speak" becomes visible. Everyone sees
 * the conversation — the server serves it without a token — but the composer is
 * replaced by a sign-in prompt for a signed-out visitor. The gate itself is the
 * server's (POST .../chat answers 401), so this is presentation, not security:
 * if a stale `can_chat` ever let the input through, the send would simply fail
 * and the same prompt would appear.
 *
 * Styled as one of the app's cream cards rather than a dark side panel, so it
 * sits in the page next to the stage instead of looking like a second site.
 */
export default function LiveStreamChat({ streamId, enabled = true, chatEnabled = true }) {
  const { status, login } = useAuth()
  const [draft, setDraft] = useState('')
  const listRef = useRef(null)
  const chat = useLiveStreamChat(streamId, { enabled })

  // Follow the conversation, but only when the reader is already at the bottom
  // — yanking the list down while someone is scrolling back is worse than
  // missing a line.
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (atBottom) el.scrollTop = el.scrollHeight
  }, [chat.messages])

  const signedIn = status === 'authenticated'
  const closed = !chatEnabled || !chat.chatEnabled
  const canType = signedIn && !closed

  async function submit(e) {
    e.preventDefault()
    if (!canType) return
    const ok = await chat.send(draft)
    if (ok) setDraft('')
  }

  return (
    <div
      className="flex h-full min-h-100 flex-col overflow-hidden rounded-3xl"
      style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.18)' }}
    >
      <div className="px-5 py-4" style={{ borderBottom: '1px solid rgba(200,146,42,.14)' }}>
        <h3 className="font-cinzel text-[11px] uppercase tracking-[.25em]" style={{ color: 'rgba(200,146,42,.85)' }}>
          Live Chat
        </h3>
        <p className="mt-1 text-xs font-light" style={{ color: '#8B7355' }}>
          {closed ? 'Chat is turned off for this session.' : 'Be kind — the astrologer reads these live.'}
        </p>
      </div>

      <div ref={listRef} className="flex-1 space-y-3.5 overflow-y-auto px-5 py-4">
        {chat.messages.length === 0 ? (
          <p className="text-xs font-light" style={{ color: '#B5A090' }}>
            {closed ? 'No chat on this session.' : 'No messages yet. Say hello.'}
          </p>
        ) : (
          chat.messages.map((m) => (
            <div key={m.id} className="text-[13px] leading-snug">
              <span className="font-semibold" style={{ color: '#C8922A' }}>
                {m.user_name}
              </span>
              <span className="ml-2" style={{ color: '#4A4036' }}>
                {m.message}
              </span>
            </div>
          ))
        )}
      </div>

      {chat.sendError && <p className="px-5 pb-1 text-[11px] text-red-600">{chat.sendError}</p>}

      {closed ? null : signedIn ? (
        <form onSubmit={submit} className="flex items-center gap-2 p-3" style={{ borderTop: '1px solid rgba(200,146,42,.14)' }}>
          <input
            value={draft}
            maxLength={500}
            onChange={(e) => {
              setDraft(e.target.value)
              if (chat.sendError) chat.clearSendError()
            }}
            placeholder="Type a message…"
            className="min-w-0 flex-1 rounded-full px-4 py-2.5 text-[13px] outline-none"
            style={{ border: '1px solid rgba(200,146,42,.22)', background: '#FDFAF6', color: '#1C140A' }}
          />
          <button
            type="submit"
            disabled={chat.sending || !draft.trim()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-[.15em] disabled:opacity-40"
            style={{ background: '#C8922A', color: '#1C140A' }}
          >
            <LuSend className="h-3.5 w-3.5" />
            Send
          </button>
        </form>
      ) : (
        <div className="p-4" style={{ borderTop: '1px solid rgba(200,146,42,.14)' }}>
          <button
            type="button"
            onClick={() => login()}
            className="flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-[11px] font-bold uppercase tracking-[.18em] transition-colors"
            style={{ background: '#1C140A', color: '#FFF' }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#C8922A'
              e.currentTarget.style.color = '#1C140A'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#1C140A'
              e.currentTarget.style.color = '#FFF'
            }}
          >
            <LuLock className="h-3.5 w-3.5" />
            Sign in to join the chat
          </button>
          <p className="mt-2.5 text-center text-[11px] font-light" style={{ color: '#B5A090' }}>
            You can keep watching without signing in.
          </p>
        </div>
      )}
    </div>
  )
}
